function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import ServiceFactory from "../../service";
import { Button } from "@progress/kendo-react-buttons";

/*
 * "How stages work" (People redesign 2026-10-09, PlunkDocs mockups board "6 · How stages work").
 * Per-tenant, per-channel rules for which signals move someone into each stage
 * (CONTACT_LIFECYCLE.md section 7). Stages are fixed (lead, prospect, customer);
 * the rules per stage are what a workspace can tune.
 *
 * CRUD: /v1/lifecycle/config (LifecycleService; writes need the tenant policy).
 * "Start from the standard ways" calls /seed-defaults.
 *
 * Rules are stored per channel (b2b, b2c). The "Apply these to" pills filter the
 * list. Under "All channels", rules that match on everything but channel are shown
 * as one line, and an edit or remove applies to every channel copy of that rule.
 */

const CHANNEL_KEYS = ["b2b", "b2c"];
const SCOPES = [["all", "All channels"], ["b2b", "Business buyers"], ["b2c", "Shoppers"]];
const CHANNEL_LABEL = {
  b2b: "Business buyers",
  b2c: "Shoppers"
};
const STAGES = [{
  key: "lead",
  one: "Lead",
  title: "Someone becomes a lead when they…"
}, {
  key: "prospect",
  one: "Prospect",
  title: "Someone becomes a prospect when they…"
}, {
  key: "customer",
  one: "Customer",
  title: "Someone becomes a customer when they…"
}];
// Rules the engine always applies; shown read-only so nobody adds a duplicate.
const SYSTEM_RULES = {
  prospect: ["Have an account number in your customer feed"],
  customer: ["Place their first order"]
};
const SOURCES = [["tracked_conversion", "Do something on your website (a form, sign up, quote)"], ["engagement", "Keep coming back or engaging"], ["feed", "Show up in a data feed"], ["webhook", "Are sent in by another system"], ["connector", "Come in through a connected app"]];
const QUALIFIER_LABEL = {
  tracked_conversion: "Event name on your website",
  engagement: "Event name (leave empty for any visit)",
  feed: "Feed name",
  webhook: "Event type the other system sends",
  connector: "Connected app name"
};
function svc(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service();
}
function activitySvc(api) {
  const Service = ServiceFactory.getType(api); // /v1/activity root
  return new Service();
}

/*
 * Rule-scoped payload field mapping (CONTACT_LIFECYCLE.md field_map): each form
 * names its fields differently, so the mapping lives on the rule as JSON
 * {role: field}. Pickers list the field names seen in the event's recent payloads
 * (90 days); free text when none were seen. "Suggest matches" fills empty roles only.
 */
const FIELD_ROLES = [["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["first_name", "First name"], ["last_name", "Last name"], ["city", "City"], ["state", "State"], ["zip", "ZIP"], ["referral_source", "How they heard about you"]];
const ROLE_ALIASES = {
  company: /company|firm|business|organi[sz]ation/i,
  email: /e-?mail/i,
  phone: /phone|mobile|tel\b/i,
  first_name: /first/i,
  last_name: /last|surname/i,
  city: /city|town/i,
  state: /\bstate|province/i,
  zip: /zip|postal/i,
  referral_source: /referral|hear|source/i
};
const TRACKED_SOURCES = ["tracked_conversion", "engagement"];
function kvTypeFor(source) {
  return source === "engagement" ? "Engagement" : "Conversion";
}
function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

/* ---------- plain sentences ---------- */

// Known event names to plain verbs. Tested against the lowercased name with
// separators removed, first match wins.
const VERBS = [[/^(sale|purchase|order|checkout|ordered)/, "Place an order"], [/(quote|rfq|estimate|pricerequest)/, "Ask for a quote"], [/(newsletter|subscribe|signup|optin|emailsignup)/, "Sign up for your newsletter"], [/(createaccount|accountcreated|register|registration|^account$|newaccount)/, "Create an account on your website"], [/(login|signin)/, "Sign in to your website"], [/(swatch|sample)/, "Ask for a sample"], [/(reprequest|salesrep|talktosales|contactsales|bookdemo|demo|meeting|appointment|consult)/, "Ask to talk with your team"], [/(application|apply)/, "Send in an application"], [/(download|brochure|catalog|whitepaper|ebook)/, "Download something from your website"], [/(cart|basket)/, "Add something to their cart"], [/(call|phone)/, "Call you"], [/(chat)/, "Start a chat with you"], [/^(lead|form|contact|contactform|contactus|inquiry|enquiry|leadform|getintouch|formsubmit|formsubmission)/, "Fill in your contact form"]];
function humanize(q) {
  const s = String(q || "").replace(/[_\-.]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\s+/g, " ").trim().toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}
function verbFor(q) {
  const k = String(q || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!k) return null;
  const hit = VERBS.find(([re]) => re.test(k));
  return hit ? hit[1] : null;
}
function times(n) {
  const v = Number(n);
  return v === 1 ? "once" : v === 2 ? "twice" : `${v} times`;
}
export function ruleSentence(r) {
  const q = (r.qualifier || "").trim();
  const hasN = r.threshold != null && r.threshold !== "" && !isNaN(Number(r.threshold)) && Number(r.threshold) > 1;
  let s;
  switch (r.source) {
    case "engagement":
      if (!q) s = hasN ? `Visit your website ${times(r.threshold)} or more` : "Visit your website";else {
        const v = verbFor(q) || `Trigger the “${humanize(q)}” event`;
        s = hasN ? `${v} ${times(r.threshold)} or more` : v;
      }
      break;
    case "feed":
      s = q ? `Show up in your “${humanize(q)}” feed` : "Show up in your customer feed";
      break;
    case "webhook":
      s = q ? `Are sent in by another system as “${humanize(q)}”` : "Are sent in by another system";
      break;
    case "connector":
      s = q ? `Come in through your ${humanize(q)} connection` : "Come in through a connected app";
      break;
    default:
      {
        const v = q ? verbFor(q) || `Trigger the “${humanize(q)}” event` : "Do something on your website";
        s = hasN ? `${v} ${times(r.threshold)} or more` : v;
      }
  }
  const lapse = Number(r.lapseDays);
  if (r.lapseDays != null && r.lapseDays !== "" && lapse > 0) s += `, counted for ${lapse} days`;
  return s;
}
function mappedCount(fieldMap) {
  try {
    return Object.keys(JSON.parse(fieldMap) || {}).length;
  } catch (e) {
    return 0;
  }
}

// Group key: everything but channel and id.
function sameRuleKey(r) {
  return [r.stage, r.source, (r.qualifier || "").trim().toLowerCase(), r.threshold == null ? "" : r.threshold, r.lapseDays == null ? "" : r.lapseDays, r.active !== false, r.fieldMap || ""].join("|");
}
const EMPTY = {
  scope: "b2b",
  stage: "lead",
  source: "tracked_conversion",
  qualifier: "",
  threshold: "",
  lapseDays: "",
  active: true,
  fieldMap: {},
  members: []
};
export default class LifecycleConfigEditor extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && !this.state.editing) this.props.onClose();
    });
    _defineProperty(this, "startAdd", stage => this.setState({
      editing: {
        ...EMPTY,
        stage,
        scope: this.state.scope,
        fieldMap: {},
        members: []
      },
      observedKeys: null,
      suggestMethod: null,
      error: null
    }));
    _defineProperty(this, "startEdit", line => {
      const r = line.rule;
      let fieldMap = {};
      try {
        fieldMap = JSON.parse(r.fieldMap) || {};
      } catch (e) {/* none */}
      this.setState({
        editing: {
          members: line.members,
          scope: this.scopeOfMembers(line.members),
          stage: r.stage,
          source: r.source,
          qualifier: r.qualifier || "",
          threshold: r.threshold == null ? "" : String(r.threshold),
          lapseDays: r.lapseDays == null ? "" : String(r.lapseDays),
          active: r.active !== false,
          fieldMap
        },
        observedKeys: null,
        suggestMethod: null,
        error: null
      }, () => this.loadObservedKeys());
    });
    _defineProperty(this, "cancel", () => this.setState({
      editing: null,
      observedKeys: null
    }));
    // Field names seen in this event's recent payloads (LifecycleService discovery
    // endpoint: tenant tracker first, central tracker fallback, the same rule the
    // stage engine uses). Falls back to the raw keyvalues endpoint.
    _defineProperty(this, "loadObservedKeys", () => {
      const ed = this.state.editing;
      if (!ed || TRACKED_SOURCES.indexOf(ed.source) < 0 || !ed.qualifier.trim()) {
        this.setState({
          observedKeys: null,
          observedMeta: null
        });
        return;
      }
      this.setState({
        keysLoading: true
      });
      svc(`/lifecycle/config/observed-keys?qualifier=${encodeURIComponent(ed.qualifier.trim())}&source=${encodeURIComponent(ed.source)}`).getAsync().then(r => {
        if (!this._alive) return;
        const keys = (r && r.keys || []).map(k => k.key).filter(Boolean);
        this.setState({
          observedKeys: keys,
          observedMeta: r ? {
            kvSource: r.kvSource,
            payloads: r.payloads
          } : null,
          keysLoading: false
        });
      }).catch(() => this.loadObservedKeysLegacy());
    });
    // Older path: distinct key names from the kv rows (end date is tomorrow because
    // the endpoint reads a bare end date as midnight).
    _defineProperty(this, "loadObservedKeysLegacy", () => {
      const ed = this.state.editing;
      if (!ed) return;
      const to = new Date();
      to.setDate(to.getDate() + 1);
      const from = new Date();
      from.setDate(from.getDate() - 90);
      activitySvc(`/metrics/keyvalues/${kvTypeFor(ed.source)}/${encodeURIComponent(ed.qualifier.trim())}/${isoDay(from)}/${isoDay(to)}`).getAsync().then(rows => {
        if (!this._alive) return;
        const keys = [...new Set((rows || []).map(r => r.keyName || r.KeyName).filter(Boolean))].sort();
        this.setState({
          observedKeys: keys,
          observedMeta: null,
          keysLoading: false
        });
      }).catch(() => {
        if (this._alive) this.setState({
          observedKeys: [],
          observedMeta: null,
          keysLoading: false
        });
      });
    });
    _defineProperty(this, "setRole", role => e => {
      const v = e.target.value;
      const fieldMap = {
        ...this.state.editing.fieldMap
      };
      if (v) fieldMap[role] = v;else delete fieldMap[role];
      this.setState({
        editing: {
          ...this.state.editing,
          fieldMap
        }
      });
    });
    // AI suggestion endpoint (server falls back to name matching when AI is not
    // available). Fills empty roles only, never replaces a manual pick. The local
    // name matcher is the offline fallback.
    _defineProperty(this, "suggestMapping", async () => {
      const {
        editing,
        observedKeys
      } = this.state;
      if (!editing || !(observedKeys || []).length) return;
      this.setState({
        suggesting: true
      });
      try {
        const r = await svc("/lifecycle/config/suggest-field-map").postAsync({
          qualifier: editing.qualifier.trim(),
          source: editing.source
        });
        if (!this._alive) return;
        const suggested = r && r.fieldMap || {};
        const fieldMap = {
          ...this.state.editing.fieldMap
        };
        const taken = new Set(Object.values(fieldMap));
        FIELD_ROLES.forEach(([role]) => {
          const k = suggested[role];
          if (fieldMap[role] || !k || taken.has(k)) return;
          fieldMap[role] = k;
          taken.add(k);
        });
        this.setState({
          editing: {
            ...this.state.editing,
            fieldMap
          },
          suggesting: false,
          suggestMethod: r && r.method
        });
      } catch (e) {
        if (!this._alive) return;
        this.setState({
          suggesting: false,
          suggestMethod: "aliases"
        });
        this.suggestMappingLocal();
      }
    });
    _defineProperty(this, "suggestMappingLocal", () => {
      const {
        editing,
        observedKeys
      } = this.state;
      if (!editing || !(observedKeys || []).length) return;
      const fieldMap = {
        ...editing.fieldMap
      };
      const taken = new Set(Object.values(fieldMap));
      FIELD_ROLES.forEach(([role]) => {
        if (fieldMap[role]) return;
        const hit = observedKeys.find(k => ROLE_ALIASES[role].test(k) && !taken.has(k));
        if (hit) {
          fieldMap[role] = hit;
          taken.add(hit);
        }
      });
      this.setState({
        editing: {
          ...editing,
          fieldMap
        }
      });
    });
    _defineProperty(this, "field", k => e => {
      const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      this.setState({
        editing: {
          ...this.state.editing,
          [k]: v
        }
      });
    });
    // One form can cover both channels: update the copies that stay, add the
    // missing ones, remove the ones no longer in scope.
    _defineProperty(this, "save", async () => {
      const ed = this.state.editing;
      if (!ed || this.state.saving) return;
      const mapped = Object.keys(ed.fieldMap || {}).length;
      const base = {
        stage: ed.stage,
        source: ed.source,
        qualifier: ed.qualifier.trim() || null,
        threshold: ed.threshold === "" ? null : Number(ed.threshold),
        lapseDays: ed.lapseDays === "" ? null : Number(ed.lapseDays),
        active: !!ed.active,
        fieldMap: mapped ? JSON.stringify(ed.fieldMap) : null
      };
      const targets = ed.scope === "all" ? CHANNEL_KEYS : [ed.scope];
      const members = ed.members || [];
      this.setState({
        saving: true,
        error: null
      });
      try {
        for (const ch of targets) {
          const existing = members.find(m => m.channel === ch);
          if (existing) await svc(`/lifecycle/config/${existing.id}`).putAsync({
            ...base,
            id: existing.id,
            channel: ch
          });else await svc("/lifecycle/config").postAsync({
            ...base,
            id: 0,
            channel: ch
          });
        }
        for (const m of members) {
          if (targets.indexOf(m.channel) < 0) await svc("/lifecycle/config").deleteAsync(m.id);
        }
        if (!this._alive) return;
        this.setState({
          editing: null,
          saving: false
        });
        this.flashSaved();
        this.load();
      } catch (e) {
        if (!this._alive) return;
        this.setState({
          saving: false,
          error: "That didn't save. Check the details and try again, or ask an admin."
        });
        this.load();
      }
    });
    _defineProperty(this, "remove", async line => {
      this.setState({
        error: null
      });
      try {
        for (const m of line.members) await svc("/lifecycle/config").deleteAsync(m.id);
        this.flashSaved();
      } catch (e) {
        this.setState({
          error: "That didn't remove. Try again, or ask an admin."
        });
      }
      this.load();
    });
    _defineProperty(this, "seedDefaults", async () => {
      if (this.state.saving) return;
      this.setState({
        saving: true,
        error: null
      });
      try {
        await svc("/lifecycle/config/seed-defaults").postAsync({});
        this.setState({
          saving: false
        });
        this.flashSaved();
        this.load();
      } catch (e) {
        this.setState({
          saving: false,
          error: "We couldn't add the standard ways. Ask an admin to try."
        });
      }
    });
    const _ch = props.channel;
    this.state = {
      rows: null,
      error: null,
      editing: null,
      saving: false,
      saved: false,
      observedKeys: null,
      keysLoading: false,
      scope: CHANNEL_KEYS.indexOf(_ch) > -1 ? _ch : "all"
    };
    this._alive = true;
  }
  componentDidMount() {
    this._alive = true;
    this.load();
    document.addEventListener("keydown", this.onKey);
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("keydown", this.onKey);
  }
  async load() {
    try {
      const rows = await svc("/lifecycle/config").getAsync();
      if (this._alive) this.setState({
        rows: rows || []
      });
    } catch (e) {
      if (this._alive) this.setState({
        rows: [],
        error: "We couldn't load your stage rules. Try again in a moment."
      });
    }
  }

  // Lines to show for one stage under the current scope.
  linesFor(stage) {
    const {
      rows,
      scope
    } = this.state;
    const mine = (rows || []).filter(r => r.stage === stage && (scope === "all" || r.channel === scope));
    if (scope !== "all") return mine.map(r => ({
      key: String(r.id),
      rule: r,
      members: [r]
    }));
    const groups = [];
    const byKey = {};
    mine.forEach(r => {
      const k = sameRuleKey(r);
      if (!byKey[k]) {
        byKey[k] = {
          key: String(r.id),
          rule: r,
          members: []
        };
        groups.push(byKey[k]);
      }
      byKey[k].members.push(r);
    });
    return groups;
  }
  scopeOfMembers(members) {
    const chs = [...new Set(members.map(m => m.channel))];
    return chs.length === 1 ? chs[0] : "all";
  }
  flashSaved() {
    this.setState({
      saved: true
    });
    clearTimeout(this._savedT);
    this._savedT = setTimeout(() => {
      if (this._alive) this.setState({
        saved: false
      });
    }, 2500);
  }
  renderForm(stage) {
    const {
      editing,
      saving,
      observedKeys,
      observedMeta,
      keysLoading,
      suggesting,
      suggestMethod
    } = this.state;
    const keys = observedKeys || [];
    const tracked = TRACKED_SOURCES.indexOf(editing.source) > -1;
    return /*#__PURE__*/React.createElement("div", {
      className: "pl-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pl-form-title"
    }, editing.members.length ? "Change this way" : "Add a way"), /*#__PURE__*/React.createElement("div", {
      className: "pl-form-grid"
    }, /*#__PURE__*/React.createElement("label", null, "Stage", /*#__PURE__*/React.createElement("select", {
      value: editing.stage,
      onChange: this.field("stage")
    }, STAGES.map(s => /*#__PURE__*/React.createElement("option", {
      key: s.key,
      value: s.key
    }, s.one)))), /*#__PURE__*/React.createElement("label", null, "Who it applies to", /*#__PURE__*/React.createElement("select", {
      value: editing.scope,
      onChange: this.field("scope")
    }, SCOPES.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l)))), /*#__PURE__*/React.createElement("label", null, "What they do", /*#__PURE__*/React.createElement("select", {
      value: editing.source,
      onChange: e => {
        this.field("source")(e);
        setTimeout(this.loadObservedKeys, 0);
      }
    }, SOURCES.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l)))), /*#__PURE__*/React.createElement("label", null, QUALIFIER_LABEL[editing.source] || "Name", /*#__PURE__*/React.createElement("input", {
      value: editing.qualifier,
      onChange: this.field("qualifier"),
      onBlur: this.loadObservedKeys,
      placeholder: "for example contact-form"
    })), /*#__PURE__*/React.createElement("label", null, "How many times (optional)", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      value: editing.threshold,
      onChange: this.field("threshold"),
      placeholder: "for example 3"
    })), /*#__PURE__*/React.createElement("label", null, "Counts for how many days (optional)", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      value: editing.lapseDays,
      onChange: this.field("lapseDays"),
      placeholder: "for example 180"
    }))), /*#__PURE__*/React.createElement("p", {
      className: "pl-form-preview"
    }, "Reads as: ", /*#__PURE__*/React.createElement("b", null, ruleSentence(editing))), editing.stage === "customer" && /*#__PURE__*/React.createElement("p", {
      className: "pl-note"
    }, "Someone becomes a customer automatically when they place their first order. Extra customer rules have no effect today."), tracked && /*#__PURE__*/React.createElement("div", {
      className: "pl-fields"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pl-fields-head"
    }, /*#__PURE__*/React.createElement("b", null, "Match form fields to contact details"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      disabled: !keys.length || suggesting,
      onClick: this.suggestMapping
    }, suggesting ? "Suggesting…" : "Suggest matches"), keysLoading ? /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "Reading recent submissions\u2026") : null, !keysLoading && suggestMethod ? /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, suggestMethod === "ai" ? "Suggested by AI. Check them before saving." : "Matched by field name.") : null), /*#__PURE__*/React.createElement("p", {
      className: "pl-muted pl-fields-hint"
    }, "This helps us recognize the same person when they come back later.", " ", keys.length ? `We saw ${keys.length} field name${keys.length === 1 ? "" : "s"} in the last 90 days` + (observedMeta && observedMeta.payloads ? ` across ${observedMeta.payloads} submissions.` : ".") : observedKeys !== null && editing.qualifier ? "We haven't seen this event recently. Type the field names in." : "Fill in the event name above to see the fields we have seen."), /*#__PURE__*/React.createElement("div", {
      className: "pl-form-grid"
    }, FIELD_ROLES.map(([role, label]) => {
      const val = (editing.fieldMap || {})[role] || "";
      const opts = val && keys.indexOf(val) < 0 ? [val, ...keys] : keys;
      return /*#__PURE__*/React.createElement("label", {
        key: role
      }, label, keys.length ? /*#__PURE__*/React.createElement("select", {
        value: val,
        onChange: this.setRole(role)
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, "Not matched"), opts.map(k => /*#__PURE__*/React.createElement("option", {
        key: k,
        value: k
      }, k))) : /*#__PURE__*/React.createElement("input", {
        value: val,
        onChange: this.setRole(role),
        placeholder: "field name"
      }));
    }))), /*#__PURE__*/React.createElement("label", {
      className: "pl-check pl-check-row"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!editing.active,
      onChange: this.field("active")
    }), /*#__PURE__*/React.createElement("span", null, "Turned on")), /*#__PURE__*/React.createElement("div", {
      className: "pl-form-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: saving,
      onClick: this.save
    }, saving ? "Saving…" : "Save"), /*#__PURE__*/React.createElement(Button, {
      onClick: this.cancel
    }, "Cancel")));
  }
  renderStage(s) {
    const {
      editing,
      scope
    } = this.state;
    const lines = this.linesFor(s.key);
    const formHere = editing && editing.stage === s.key;
    const editingKey = editing && editing.members.length ? String(editing.members[0].id) : null;
    return /*#__PURE__*/React.createElement("section", {
      key: s.key,
      className: "pl-card pl-rule-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pl-rule-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: `pl-chip pl-chip-${s.key}`
    }, s.one), /*#__PURE__*/React.createElement("b", null, s.title), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pl-add",
      onClick: () => this.startAdd(s.key)
    }, "+ Add a way")), lines.map(line => {
      const r = line.rule;
      const chs = [...new Set(line.members.map(m => m.channel))];
      const tags = [];
      if (scope === "all" && chs.length === 1) tags.push(`${CHANNEL_LABEL[chs[0]] || "One channel"} only`);
      if (r.active === false) tags.push("Turned off");
      const n = mappedCount(r.fieldMap);
      if (n) tags.push(`${n} field${n === 1 ? "" : "s"} matched`);
      if (editingKey && line.members.some(m => String(m.id) === editingKey)) return null;
      return /*#__PURE__*/React.createElement("div", {
        key: line.key,
        className: `pl-rule${r.active === false ? " is-off" : ""}`
      }, /*#__PURE__*/React.createElement("span", {
        className: "pl-rule-text"
      }, ruleSentence(r), tags.map(t => /*#__PURE__*/React.createElement("span", {
        key: t,
        className: "pl-tag"
      }, t))), /*#__PURE__*/React.createElement("a", {
        className: "pl-link",
        onClick: () => this.startEdit(line)
      }, "Edit"), /*#__PURE__*/React.createElement("a", {
        className: "pl-link pl-link-muted",
        onClick: () => this.remove(line)
      }, "Remove"));
    }), (SYSTEM_RULES[s.key] || []).map(t => /*#__PURE__*/React.createElement("div", {
      key: t,
      className: "pl-rule pl-rule-system"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pl-rule-text"
    }, t), /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "Always on"))), lines.length === 0 && !(SYSTEM_RULES[s.key] || []).length && !formHere ? /*#__PURE__*/React.createElement("div", {
      className: "pl-rule pl-rule-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "No ways yet. Add one to start.")) : null, formHere ? this.renderForm(s.key) : null);
  }
  render() {
    const {
      rows,
      error,
      saving,
      saved,
      scope
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "pl-rules",
      role: "dialog",
      "aria-label": "How stages work"
    }, this.props.hideTitle ? saving || saved ? /*#__PURE__*/React.createElement("div", {
      className: "pl-rules-status"
    }, saving ? /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "Saving\u2026") : /*#__PURE__*/React.createElement("span", {
      className: "pl-up"
    }, "Saved")) : null : /*#__PURE__*/React.createElement("div", {
      className: "pl-rules-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pl-rules-title"
    }, /*#__PURE__*/React.createElement("span", null, "How stages work"), /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "The things that move someone from lead to prospect to customer")), /*#__PURE__*/React.createElement("span", {
      className: "pl-flex"
    }), saving ? /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, "Saving\u2026") : saved ? /*#__PURE__*/React.createElement("span", {
      className: "pl-up"
    }, "Saved") : null), /*#__PURE__*/React.createElement("div", {
      className: "pl-rules-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pl-rules-col"
    }, error && /*#__PURE__*/React.createElement("p", {
      className: "pl-error"
    }, error), rows === null ? /*#__PURE__*/React.createElement("p", {
      className: "pl-muted"
    }, "Loading\u2026") : /*#__PURE__*/React.createElement(React.Fragment, null, rows.length === 0 && /*#__PURE__*/React.createElement("section", {
      className: "pl-card pl-seed"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pl-flex"
    }, "You are using the standard ways for now. Add them here to change them."), /*#__PURE__*/React.createElement(Button, {
      disabled: saving,
      onClick: this.seedDefaults
    }, saving ? "Adding…" : "Start from the standard ways")), STAGES.map(s => this.renderStage(s)), /*#__PURE__*/React.createElement("section", {
      className: "pl-card pl-scope"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pl-flex"
    }, "Apply these to"), SCOPES.map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      key: v,
      type: "button",
      className: `pl-pill${scope === v ? " is-on" : ""}`,
      "aria-pressed": scope === v,
      onClick: () => this.setState({
        scope: v
      })
    }, l))), /*#__PURE__*/React.createElement("span", {
      className: "pl-foot"
    }, "People only move forward. Changes apply to new activity from now on.")))));
  }
}